import { useState, useEffect, useCallback } from 'react';
import { getSimAccount, type SimAccount } from '@/api/sim';

const POLL_MS = 1_000;

/**
 * useRealtimeAccount — polls /api/sim/account every 1s.
 *
 * Backend enrichAccount() reads each position's currentPrice from Redis
 * (written ~1/s by the ETL), so totalValue / totalMarketValue reflect live
 * market prices. Tab-hidden tabs skip the refresh (visibilitychange).
 */
export function useRealtimeAccount() {
  const [account, setAccount] = useState<SimAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    return getSimAccount()
      .then(d => setAccount(d))
      .catch(() => { /* keep last known value */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().finally(() => { if (!cancelled) setLoading(false); });

    const tick = () => {
      if (document.visibilityState === 'visible') load();
    };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  return { account, loading, refresh: load };
}
